import { IconX } from "@tabler/icons-react";
import { useState } from "react";
import { createRoot } from "react-dom/client";
import { androidPlayUrl, isAndroidPhoneBrowser, isHomepagePath } from "../appStoreLinks";
import { currentStoreReferral } from "../attribution";

// "Get the app" bar for Android phones (2026-11-02), the same idea as the
// Smart App Banner Safari draws on iPhone from the `apple-itunes-app` meta
// tag (gatsby-ssr). Chrome has no built-in one, so this draws it: the Eden
// icon, "Free on Google Play", and a Get button, at the very top of the page
// above the sticky nav. Closing it keeps it away for 30 days.
//
// Mounted once in its own root from gatsby-browser, before the page, so it
// pushes the page down instead of covering the nav. The server never renders
// it, so there is nothing to hydrate.

const DISMISS_KEY = "eden-android-app-banner-dismissed";
const DISMISS_DAYS = 30;

function dismissedRecently(): boolean {
  try {
    const at = Number(window.localStorage.getItem(DISMISS_KEY));
    return at > 0 && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function rememberDismissed() {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Private mode or blocked storage: it just shows again next visit.
  }
}

function AndroidAppBanner() {
  const [open, setOpen] = useState(true);
  // Carries a referral link's code to the app (androidPlayUrl).
  const [href] = useState(() => androidPlayUrl("banner", currentStoreReferral()));
  if (!open) return null;

  return (
    <div className="relative z-[101] flex items-center gap-3 border-b border-border bg-card px-3 py-2.5 text-text-primary">
      <button
        type="button"
        aria-label="Close"
        onClick={() => {
          rememberDismissed();
          setOpen(false);
        }}
        className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-text-muted"
      >
        <IconX size={16} stroke={2} aria-hidden />
      </button>
      <img
        src="/icon-192.png"
        alt=""
        width={40}
        height={40}
        className="h-10 w-10 shrink-0 rounded-[10px]"
      />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-[0.9rem] font-semibold">Eden</div>
        <div className="truncate text-[0.78rem] text-text-secondary">Free on Google Play</div>
      </div>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 rounded-full bg-accent px-4 py-1.5 text-[0.85rem] font-semibold text-page"
      >
        Get
      </a>
    </div>
  );
}

const HOST_ID = "eden-android-app-banner";

/** Called once from gatsby-browser's onInitialClientRender. */
export function mountAndroidAppBanner() {
  if (!isAndroidPhoneBrowser(navigator.userAgent) || dismissedRecently()) return;
  if (document.getElementById(HOST_ID)) return;
  const host = document.createElement("div");
  host.id = HOST_ID;
  document.body.prepend(host);
  createRoot(host).render(<AndroidAppBanner />);
  syncAndroidAppBannerForLocation();
}

/**
 * Hidden on the homepage, whose own buttons already go to Google Play
 * (isHomepagePath). Runs on every client-side navigation (gatsby-browser
 * onRouteUpdate), so it comes back on the next page.
 */
export function syncAndroidAppBannerForLocation() {
  const host = document.getElementById(HOST_ID);
  if (host) host.hidden = isHomepagePath(window.location.pathname);
}
